import React, { useState, useEffect, useCallback } from "react";
import { useSearchParams, useNavigate } from "react-router-dom";
import * as func from "../../api/apiHelper";
import * as urls from "../../api/urlHelper";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faBox,
  faUsers,
  faBell,
  faChevronLeft,
  faChevronRight,
} from "@fortawesome/free-solid-svg-icons";
import { toast } from "react-toastify";

const PACK_CONFIG = {
  free:          { label: "Free",          color: "bg-gray-100 text-gray-600",    border: "border-gray-300" },
  spark_bundle:  { label: "Spark Bundle",  color: "bg-blue-100 text-blue-700",    border: "border-blue-400" },
  premium:       { label: "Premium",       color: "bg-indigo-100 text-indigo-700", border: "border-indigo-400" },
  elite:         { label: "Elite",         color: "bg-purple-100 text-purple-700", border: "border-purple-400" },
  annual:        { label: "Annual",        color: "bg-yellow-100 text-yellow-700", border: "border-[#C9A84C]" },
  single_unlock: { label: "Single Unlock", color: "bg-teal-100 text-teal-700",    border: "border-teal-400" },
};

const ALL_PACKS = Object.keys(PACK_CONFIG);

const PackBadge = ({ packType }) => {
  const cfg = PACK_CONFIG[packType] || { label: packType, color: "bg-gray-100 text-gray-600" };
  return (
    <span className={`inline-flex items-center px-2.5 py-1 rounded-full text-xs font-semibold ${cfg.color}`}>
      {cfg.label}
    </span>
  );
};

const PackUsers = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const navigate = useNavigate();
  const packType = searchParams.get("packType") || "free";

  const [users, setUsers]           = useState([]);
  const [loading, setLoading]       = useState(true);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [total, setTotal]           = useState(0);
  const limit = 20;

  const fetchUsers = useCallback(async (page, pt) => {
    setLoading(true);
    try {
      const params = { packType: pt, page, limit };
      const res = await func.get(urls.LIST_USERS_BY_PACK_URL, { params });
      if (res?.status === 200) {
        setUsers(res.data.packs || []);
        setTotalPages(res.data.totalPages || 1);
        setTotal(res.data.total || 0);
      }
    } catch {
      toast.error("Failed to load users");
    } finally {
      setLoading(false);
    }
  }, [limit]);

  useEffect(() => {
    setCurrentPage(1);
    fetchUsers(1, packType);
  }, [packType, fetchUsers]);

  const switchPack = (pt) => {
    setSearchParams({ packType: pt });
  };

  const goToPage = (p) => {
    if (p < 1 || p > totalPages) return;
    setCurrentPage(p);
    fetchUsers(p, packType);
  };

  const cfg = PACK_CONFIG[packType] || {};

  return (
    <div className="p-6 space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-xl font-bold text-gray-900 flex items-center gap-2">
            <FontAwesomeIcon icon={faBox} className="text-[#C9A84C]" />
            Users by Pack
          </h1>
          <p className="text-sm text-gray-400 mt-0.5">Drill into any pack segment</p>
        </div>
        <button
          onClick={() => navigate(`/notifications/broadcast`)}
          className="flex items-center gap-2 px-4 py-2 bg-[#C9A84C] text-white rounded-lg text-sm font-medium hover:bg-[#b8953f] transition"
        >
          <FontAwesomeIcon icon={faBell} className="text-xs" />
          Notify This Segment
        </button>
      </div>

      {/* Pack selector tabs */}
      <div className="flex flex-wrap gap-2">
        {ALL_PACKS.map((pt) => {
          const c = PACK_CONFIG[pt];
          const active = packType === pt;
          return (
            <button
              key={pt}
              onClick={() => switchPack(pt)}
              className={`px-4 py-2 rounded-full text-sm font-medium border transition ${
                active ? `${c.color} ${c.border} border` : "bg-white text-gray-500 border-gray-200 hover:bg-gray-50"
              }`}
            >
              {c.label}
            </button>
          );
        })}
      </div>

      {/* Table */}
      <div className={`bg-white rounded-xl border overflow-hidden ${cfg.border || "border-gray-100"}`}>
        <div className="px-5 py-4 border-b border-gray-50 flex items-center justify-between">
          <div className="flex items-center gap-2">
            <FontAwesomeIcon icon={faUsers} className="text-gray-400 text-sm" />
            <p className="text-sm font-semibold text-gray-700">
              {total} users on <PackBadge packType={packType} />
            </p>
          </div>
        </div>

        {loading ? (
          <div className="flex justify-center py-16">
            <div className="w-8 h-8 border-2 border-[#C9A84C] border-t-transparent rounded-full animate-spin" />
          </div>
        ) : users.length === 0 ? (
          <div className="text-center py-16 text-gray-400 text-sm">No users on this pack</div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-gray-50 border-b border-gray-100">
                <tr>
                  <th className="text-left px-5 py-3 text-xs font-semibold text-gray-400 uppercase tracking-wider">Name</th>
                  <th className="text-left px-5 py-3 text-xs font-semibold text-gray-400 uppercase tracking-wider">Email</th>
                  <th className="text-left px-5 py-3 text-xs font-semibold text-gray-400 uppercase tracking-wider">Pack Expiry</th>
                  <th className="text-left px-5 py-3 text-xs font-semibold text-gray-400 uppercase tracking-wider">Last Updated</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-50">
                {users.map((u) => (
                  <tr key={u._id} className="hover:bg-gray-50 transition">
                    <td className="px-5 py-3.5 font-medium text-gray-800">
                      {u.seeker?.fullName || <span className="text-gray-400 italic">No profile</span>}
                    </td>
                    <td className="px-5 py-3.5 text-gray-500">{u.seeker?.emailId || "—"}</td>
                    <td className="px-5 py-3.5 text-xs text-gray-500">
                      {u.packExpiry
                        ? new Date(u.packExpiry).toLocaleDateString("en-IN", { day: "numeric", month: "short", year: "numeric" })
                        : <span className="text-gray-400">Never</span>}
                    </td>
                    <td className="px-5 py-3.5 text-xs text-gray-400">
                      {new Date(u.updatedAt).toLocaleDateString("en-IN", { day: "numeric", month: "short", year: "numeric" })}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

        {totalPages > 1 && (
          <div className="px-5 py-4 border-t border-gray-50 flex items-center justify-between">
            <p className="text-xs text-gray-400">Page {currentPage} of {totalPages}</p>
            <div className="flex items-center gap-2">
              <button onClick={() => goToPage(currentPage - 1)} disabled={currentPage === 1}
                className="w-8 h-8 rounded-lg flex items-center justify-center border border-gray-200 text-gray-500 hover:bg-gray-50 disabled:opacity-40 disabled:cursor-not-allowed">
                <FontAwesomeIcon icon={faChevronLeft} className="text-xs" />
              </button>
              <button onClick={() => goToPage(currentPage + 1)} disabled={currentPage === totalPages}
                className="w-8 h-8 rounded-lg flex items-center justify-center border border-gray-200 text-gray-500 hover:bg-gray-50 disabled:opacity-40 disabled:cursor-not-allowed">
                <FontAwesomeIcon icon={faChevronRight} className="text-xs" />
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default PackUsers;
